import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { graphql } from '@apollo/client/react/hoc';
import { compose } from 'recompose';
import { useCookies } from 'react-cookie';
import styled from 'styled-components';
import { ContentState, convertFromHTML, Editor, EditorState } from 'draft-js';
import 'draft-js/dist/Draft.css';
import { AuthConsumer } from '../auth/AuthContext';
import { JWT_COOKIE_KEY } from '../../../consts';
import {
  Box,
  Button,
  Card,
  ErrorComponent,
  FieldRow,
  Loader,
  StyledInput,
  TitleBar,
  WrappedQuery,
} from '../../../components/shared';
import { ACKNOWLEDGE_DECLARATION, GET_LATEST_PORTAL_DECLARATION } from '../../../graphql';
import { PageInner } from '../../../layouts';
import { theme } from '../../../theme';
import { Label } from '../../../typography';
import { formatDeclarationData, writeToIndexedDB } from '../../../utils';
import { SCREEN } from '../../../theme/breakpoints';

const isWeb = SCREEN.isWeb;

type Props = {
  acknowledgeDeclaration?: Function;
  openHandler: Dispatch<SetStateAction<boolean>>;
};

const EditorContainer = styled.div`
  border: 1px solid #ccc;
  border-radius: 4px;
  color: primary;
  cursor: text;
  margin-right: 16px;
  min-height: 6em;
  width: 100%;
`;

const DeclarationContainer: FC<Props> = props => {
  // @ts-ignore
  const [, setCookie] = useCookies([`${JWT_COOKIE_KEY}`]);
  const editor = useRef(null);
  const [editorState, setEditorState] = useState(() => EditorState.createEmpty());
  const [declaration, setDeclaration] = useState<any>(undefined);
  const [acknowledgedDeclarationLocal, setAcknowledgedDeclarationLocal] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (declaration) {
      const blocksFromHTML = convertFromHTML(declaration.text);
      const state = ContentState.createFromBlockArray(blocksFromHTML.contentBlocks, blocksFromHTML.entityMap);
      setEditorState(() => EditorState.createWithContent(state));
    }
  }, [declaration]);

  const focusEditor = () => {
    // @ts-ignore
    editor && editor.current && editor.current.focus();
  };

  return (
    <AuthConsumer>
      {authContext => {
        return (
          <PageInner maxWidth="1200px" style={{ margin: '0 auto' }}>
            <Card p={4}>
              <WrappedQuery query={GET_LATEST_PORTAL_DECLARATION} variables={{}} overrideStates={true}>
                {({ data, loading, error }) => {
                  if (loading) {
                    return <Loader />;
                  }

                  if (error || !data.declaration) {
                    return null;
                  }

                  if (data.declaration && !declaration) {
                    setDeclaration(formatDeclarationData(data.declaration));
                  }

                  return declaration ? (
                    <>
                      <TitleBar>{declaration.title}</TitleBar>
                      <EditorContainer onClick={focusEditor}>
                        <Editor
                          ref={editor}
                          editorState={editorState}
                          onChange={(data: any) => {}}
                          readOnly={declaration.published}
                          placeholder="Write something!"
                        />
                      </EditorContainer>
                      <Box
                        height="45px"
                        width="100%"
                        display={isWeb ? 'block' : 'flex'}
                        flexDirection={isWeb ? 'column' : undefined}
                      >
                        <StyledInput
                          type="checkbox"
                          onChange={() => setAcknowledgedDeclarationLocal(!acknowledgedDeclarationLocal)}
                          onClick={() => setAcknowledgedDeclarationLocal(!acknowledgedDeclarationLocal)}
                          defaultChecked={acknowledgedDeclarationLocal}
                          wrapperTop="12px"
                          wrapperDisplay="inline-block"
                          wrapperWidth={isWeb ? '40px' : '18px'}
                        />
                        <div
                          style={{
                            display: isWeb ? 'inline-block' : 'flex',
                            ...(isWeb && { height: '40px' }),
                            ...(!isWeb && { flexDirection: 'column' }),
                            ...(!isWeb && { marginTop: '-15px' }),
                            ...(!isWeb && { paddingLeft: '25px' }),
                          }}
                        >
                          <Label ml={isWeb ? '20px' : '0px'}>I have read this declaration and I agree.</Label>
                        </div>
                      </Box>
                      <FieldRow flexDirection="row" justifyContent="flex-end">
                        <Button
                          mr={4}
                          bg={theme.colors.success}
                          type="submit"
                          isLoading={submitting}
                          text="OK"
                          width="40px"
                          onClick={async () => {
                            setSubmitting(true);
                            props.acknowledgeDeclaration &&
                              (await props
                                .acknowledgeDeclaration({
                                  variables: {
                                    input: {
                                      acknowledgedDeclaration: acknowledgedDeclarationLocal,
                                      declarationId: declaration.id,
                                    },
                                  },
                                })
                                .then(({ data }: any) => {
                                  writeToIndexedDB(JWT_COOKIE_KEY, data.acknowledgeDeclaration.token);
                                  // @ts-ignore
                                  setCookie(`${JWT_COOKIE_KEY}`, data.acknowledgeDeclaration.token, {
                                    path: '/',
                                    maxAge: 28800, // Will expire after 8hr (value is in number of sec.)
                                  });
                                  authContext.setAcknowledgeDeclaration(acknowledgedDeclarationLocal);
                                  props.openHandler(false);
                                  setSubmitting(false);
                                })
                                .catch((err: any) => {
                                  setSubmitting(false);
                                  setErrorMessage(err.message);
                                }));
                          }}
                        />
                      </FieldRow>
                      {errorMessage && <ErrorComponent message={errorMessage} />}
                    </>
                  ) : null;
                }}
              </WrappedQuery>
            </Card>
          </PageInner>
        );
      }}
    </AuthConsumer>
  );
};

export default compose<any, Props>(
  graphql(ACKNOWLEDGE_DECLARATION, {
    options: () => {
      return {
        refetchQueries: [],
      };
    },
    name: 'acknowledgeDeclaration',
  })
)(DeclarationContainer);
